import React, { useCallback, useEffect, useRef, useState } from "react";
import { FileUp, KeyRound, ListChecks, RefreshCw, Upload, Users } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useRelease } from "../lib/release";

const when = (iso) =>
  iso ? new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "—";

/** Legacy portal migration: identify the old password recipe, dry run, then import for real. */
export default function AdminMigration() {
  const { isOn } = useRelease();
  const devRefreshOn = isOn("feature.dev_data_refresh");
  const importOn = isOn("feature.migration_import");
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState("");
  const [sendInvites, setSendInvites] = useState(true);
  const [runs, setRuns] = useState([]);
  const [probe, setProbe] = useState({ password: "", stored_hash: "", salt: "" });
  const [probeResult, setProbeResult] = useState(null);
  const fileInput = useRef(null);

  const loadRuns = useCallback(async () => {
    const { data } = await api.get("/admin/migration/runs");
    setRuns(data.runs);
  }, []);

  useEffect(() => { loadRuns().catch(() => {}); }, [loadRuns]);

  const pickFile = (event) => {
    setFile(event.target.files?.[0] || null);
    setReport(null);
    setResult(null);
  };

  const send = async (path, extra) => {
    if (!file) { toast.error("Choose the legacy export file first"); return null; }
    const body = new FormData();
    body.append("file", file);
    Object.entries(extra || {}).forEach(([k, v]) => body.append(k, v));
    const { data } = await api.post(path, body, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  };

  const dryRun = async () => {
    setBusy("dry");
    try {
      const data = await send("/admin/migration/preview");
      if (data) { setReport(data); setResult(null); toast.success("Dry run complete — nothing was written"); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const importForReal = async () => {
    if (!window.confirm(`Create ${report?.to_create || 0} account(s) in this environment's database? This writes data.`)) return;
    setBusy("import");
    try {
      const data = await send("/admin/migration/import", { send_invites: sendInvites });
      if (data) {
        setResult(data);
        toast.success(`${data.created} account(s) created`);
        loadRuns();
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const identify = async (event) => {
    event.preventDefault();
    setBusy("probe");
    try {
      const { data } = await api.post("/admin/migration/identify-hash", {
        password: probe.password, stored_hash: probe.stored_hash.trim(), salt: probe.salt.trim(),
      });
      setProbeResult(data);
      if (data.scheme) toast.success(`Matched: ${data.scheme}`);
      else toast.error("No known recipe matched this digest");
      setProbe({ ...probe, password: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <div className="mx-auto max-w-[1400px] px-6 py-10 md:py-12 lg:px-10" data-testid="admin-migration-page">
      <div className="eyebrow mb-2">Legacy portal · User migration</div>
      <h1 className="font-heading font-black text-4xl md:text-5xl leading-none tracking-tight mb-3" style={{ color: "var(--ink)" }}>
        Migration
      </h1>
      <p className="text-sm max-w-3xl mb-10" style={{ color: "var(--muted)" }}>
        Import the legacy portal's users with their current passwords intact. Identify the old
        password recipe once, run the export in report-only mode, fix any data issues, then import
        for real. A carried-over password that fails the current policy still signs in, but the user
        must set a new one before the session continues.
      </p>

      {/* Hash identification */}
      <section className="border p-6 mb-10" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
        <h2 className="font-heading font-bold text-2xl mb-1 flex items-center gap-2" style={{ color: "var(--ink)" }}>
          <KeyRound size={18} style={{ color: "var(--muted)" }}/> Identify the legacy recipe
        </h2>
        <p className="text-xs mb-5 max-w-2xl" style={{ color: "var(--muted)" }}>
          Paste one account's stored 64-character digest and the password you know for it. The
          password is used for the comparison only and is never stored.
        </p>
        <form onSubmit={identify} className="grid gap-5 md:grid-cols-3" data-testid="hash-probe-form">
          <div>
            <label className="field-label">Known password</label>
            <input type="password" className="w-full border px-3 py-2 text-sm outline-none"
                   style={{ borderColor: "var(--border-strong)", background: "var(--bg)" }}
                   value={probe.password} onChange={(e) => setProbe({ ...probe, password: e.target.value })}
                   required data-testid="probe-password"/>
          </div>
          <div>
            <label className="field-label">Stored hash</label>
            <input className="w-full border px-3 py-2 font-mono text-xs outline-none"
                   style={{ borderColor: "var(--border-strong)", background: "var(--bg)" }}
                   value={probe.stored_hash} onChange={(e) => setProbe({ ...probe, stored_hash: e.target.value })}
                   placeholder="64 hex characters" required data-testid="probe-hash"/>
          </div>
          <div>
            <label className="field-label">Salt (if the export has one)</label>
            <input className="w-full border px-3 py-2 font-mono text-xs outline-none"
                   style={{ borderColor: "var(--border-strong)", background: "var(--bg)" }}
                   value={probe.salt} onChange={(e) => setProbe({ ...probe, salt: e.target.value })}
                   data-testid="probe-salt"/>
          </div>
          <div className="md:col-span-3">
            <button type="submit" className="btn-primary" disabled={busy === "probe"} data-testid="probe-submit">
              {busy === "probe" ? "Testing recipes…" : "Test recipes"}
            </button>
          </div>
        </form>
        {probeResult && (
          <div className="mt-5 border p-4 text-sm" style={{ borderColor: "var(--border)", background: "var(--bg)" }} data-testid="probe-result">
            <div className="font-semibold" style={{ color: "var(--ink)" }}>
              {probeResult.scheme ? `Matched recipe: ${probeResult.scheme}` : "No match"}
            </div>
            <p className="mt-1 text-xs" style={{ color: "var(--muted)" }}>{probeResult.message}</p>
            <p className="mt-2 text-xs font-mono" style={{ color: "var(--muted)" }}>
              Tried: {probeResult.recipes_tried.join(", ")}
            </p>
          </div>
        )}
      </section>

      {/* Upload + dry run */}
      <section className="border p-6 mb-10" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
        <h2 className="font-heading font-bold text-2xl mb-1 flex items-center gap-2" style={{ color: "var(--ink)" }}>
          <FileUp size={18} style={{ color: "var(--muted)" }}/> Legacy export
        </h2>
        <p className="text-xs mb-5 max-w-2xl" style={{ color: "var(--muted)" }}>
          CSV or JSON containing email, username, name, company, roles, approver flag and the stored
          password hash (plus its salt column, if any). Column names are matched flexibly.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <input ref={fileInput} type="file" accept=".csv,.json,.txt" onChange={pickFile}
                 className="text-sm" data-testid="migration-file-input"/>
          <button onClick={dryRun} className="btn-ghost flex items-center gap-2" disabled={!file || busy === "dry"} data-testid="migration-dry-run">
            <ListChecks size={14}/> {busy === "dry" ? "Checking…" : "Run dry run"}
          </button>
        </div>

        {report && (
          <div className="mt-6" data-testid="migration-report">
            <div className="grid grid-cols-2 border md:grid-cols-5" style={{ borderColor: "var(--border)" }}>
              <Cell label="Rows read" value={report.rows_read} testId="report-rows-read"/>
              <Cell label="Would create" value={report.to_create} testId="report-to-create"/>
              <Cell label="Skipped" value={report.skipped} testId="report-skipped"/>
              <Cell label="Passwords carried" value={report.passwords_carried} testId="report-carried"/>
              <Cell label="Need reset" value={report.passwords_reset_required} testId="report-reset"/>
            </div>
            <p className="mt-3 text-xs" style={{ color: "var(--muted)" }}>
              Legacy recipe: <span className="font-mono">{report.legacy_scheme}</span>
            </p>

            {Boolean(report.issues?.length) && (
              <IssueTable title="Rows that will be skipped" rows={report.issues.map((i) => ({
                row: i.row, email: i.email, detail: i.issues.join("; "),
              }))} testId="report-issues"/>
            )}
            {Boolean(report.role_warnings?.length) && (
              <IssueTable title="Role mapping warnings" rows={report.role_warnings.map((w) => ({
                row: w.row, email: w.email, detail: w.warning,
              }))} testId="report-role-warnings"/>
            )}

            {Boolean(report.preview?.length) && (
              <div className="mt-6 border overflow-x-auto" style={{ borderColor: "var(--border)" }}>
                <table className="w-full text-sm min-w-[760px]" data-testid="report-preview-table">
                  <thead>
                    <tr className="border-b eyebrow text-left" style={{ borderColor: "var(--border)" }}>
                      <th className="px-4 py-3">Email</th>
                      <th className="px-4 py-3">Username</th>
                      <th className="px-4 py-3">Name</th>
                      <th className="px-4 py-3">Company</th>
                      <th className="px-4 py-3">Role</th>
                      <th className="px-4 py-3">Approver</th>
                      <th className="px-4 py-3">Password</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.preview.map((row) => (
                      <tr key={row.row} className="border-b" style={{ borderColor: "var(--border)" }}>
                        <td className="px-4 py-2.5" style={{ color: "var(--ink)" }}>{row.work_email}</td>
                        <td className="px-4 py-2.5" style={{ color: "var(--muted)" }}>{row.username || "auto"}</td>
                        <td className="px-4 py-2.5" style={{ color: "var(--muted)" }}>{row.name}</td>
                        <td className="px-4 py-2.5" style={{ color: "var(--muted)" }}>{row.company || "—"}</td>
                        <td className="px-4 py-2.5" style={{ color: "var(--muted)" }}>{row.roles.join(", ")}</td>
                        <td className="px-4 py-2.5" style={{ color: "var(--muted)" }}>{row.is_approver ? "Yes" : "No"}</td>
                        <td className="px-4 py-2.5">
                          <span className="pill text-[10px]">{row.password_carried ? "Carried over" : "Reset required"}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-8 border p-5" style={{ borderColor: "var(--border-strong)", background: "var(--bg)" }}>
              {!importOn && (
                <p className="mb-4 text-xs" style={{ color: "var(--muted)" }} data-testid="migration-import-off">
                  The real import is switched off in this environment. Dry runs still work.
                </p>
              )}
              <label className="flex items-center gap-2 text-sm mb-4" style={{ color: "var(--ink)" }}>
                <input type="checkbox" checked={sendInvites}
                       onChange={(e) => setSendInvites(e.target.checked)}
                       data-testid="migration-send-invites"/>
                Email a set-password invitation to users whose password cannot be carried over
              </label>
              <button onClick={importForReal} className="btn-primary flex items-center gap-2"
                      disabled={!report.to_create || busy === "import" || !importOn} data-testid="migration-import">
                <Upload size={14}/> {busy === "import" ? "Importing…" : `Import ${report.to_create} account(s) for real`}
              </button>
            </div>
          </div>
        )}

        {result && (
          <div className="mt-6 border p-5" style={{ borderColor: "var(--border)", background: "var(--bg)" }} data-testid="migration-result">
            <div className="font-semibold mb-2" style={{ color: "var(--ink)" }}>
              Import {result.run_id} complete
            </div>
            <p className="text-sm" style={{ color: "var(--muted)" }}>
              {result.created} created · {result.passwords_carried} with their legacy password ·{" "}
              {result.invited} invitation(s) sent · {result.skipped} skipped.
            </p>
            {Boolean(result.failures?.length) && (
              <IssueTable title="Failures" rows={result.failures.map((f) => ({
                row: f.row, email: f.email, detail: f.error,
              }))} testId="result-failures"/>
            )}
          </div>
        )}
      </section>

      {/* Dev data refresh */}
      {devRefreshOn && <DevRefreshSection />}

      {/* Past runs */}
      <section>
        <h2 className="font-heading font-bold text-2xl mb-4 flex items-center gap-2" style={{ color: "var(--ink)" }}>
          <Users size={18} style={{ color: "var(--muted)" }}/> Previous imports
        </h2>
        <div className="border overflow-x-auto" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
          <table className="w-full text-sm min-w-[640px]" data-testid="migration-runs-table">
            <thead>
              <tr className="border-b eyebrow text-left" style={{ borderColor: "var(--border)" }}>
                <th className="px-4 py-3">Run</th>
                <th className="px-4 py-3">File</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3">Carried</th>
                <th className="px-4 py-3">Invited</th>
                <th className="px-4 py-3">Skipped</th>
                <th className="px-4 py-3">By</th>
                <th className="px-4 py-3">When</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <tr key={run.run_id} className="border-b" style={{ borderColor: "var(--border)" }} data-testid={`migration-run-${run.run_id}`}>
                  <td className="px-4 py-3 font-mono text-xs" style={{ color: "var(--ink)" }}>{run.run_id}</td>
                  <td className="px-4 py-3" style={{ color: "var(--muted)" }}>{run.file}</td>
                  <td className="px-4 py-3" style={{ color: "var(--muted)" }}>{run.created}</td>
                  <td className="px-4 py-3" style={{ color: "var(--muted)" }}>{run.passwords_carried}</td>
                  <td className="px-4 py-3" style={{ color: "var(--muted)" }}>{run.invited}</td>
                  <td className="px-4 py-3" style={{ color: "var(--muted)" }}>{run.skipped}</td>
                  <td className="px-4 py-3" style={{ color: "var(--muted)" }}>{run.ran_by}</td>
                  <td className="px-4 py-3" style={{ color: "var(--muted)" }}>{when(run.ran_at)}</td>
                </tr>
              ))}
              {!runs.length && (
                <tr><td colSpan={8} className="p-10 text-center text-sm" style={{ color: "var(--muted)" }} data-testid="migration-runs-empty">
                  No imports have been run in this environment.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function DevRefreshSection() {
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ source_mongo_url: "", source_db: "", in_place: false, confirm: "" });
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/admin/dev/refresh-info").then(({ data }) => setInfo(data)).catch(() => setInfo(null));
  }, []);

  const run = async (dry) => {
    if (!dry && !window.confirm("Replace this environment's database with a scrubbed copy? Existing dev data is dropped.")) return;
    setBusy(dry ? "dry" : "run");
    try {
      const { data } = await api.post("/admin/dev/refresh", { ...form, dry_run: dry });
      setReport(data);
      toast.success(dry ? "Report only — nothing was written" : `${data.documents_written} document(s) refreshed`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  if (!info) return null;

  return (
    <section className="border p-6 mb-10" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="dev-refresh-section">
      <h2 className="font-heading font-bold text-2xl mb-1 flex items-center gap-2" style={{ color: "var(--ink)" }}>
        <RefreshCw size={18} style={{ color: "var(--muted)" }}/> Refresh dev data from production
      </h2>
      <p className="text-xs mb-5 max-w-2xl" style={{ color: "var(--muted)" }}>
        Copies the source database into <span className="font-mono">{info.target_db}</span> with every
        person's name, username and email address and every employer name replaced by a stable
        placeholder. Dollar amounts are copied unchanged. The audit trail, sessions, email codes and
        stored PDFs are never copied, and scrubbed accounts have no password — only{" "}
        {info.accounts_preserved.join(" and ")} can sign in afterwards.
      </p>

      {!info.allowed ? (
        <div className="pill-danger inline-block px-3 py-2 text-sm" data-testid="dev-refresh-blocked">
          This is a production deployment. A data refresh is not available here.
        </div>
      ) : (
        <>
          <div className="grid gap-5 md:grid-cols-3">
            <div>
              <label className="field-label">Source Mongo URL</label>
              <input className="w-full border px-3 py-2 font-mono text-xs outline-none"
                     style={{ borderColor: "var(--border-strong)", background: "var(--bg)" }}
                     value={form.source_mongo_url} disabled={form.in_place}
                     onChange={(e) => setForm({ ...form, source_mongo_url: e.target.value })}
                     placeholder={info.source_configured ? "using SOURCE_MONGO_URL" : "mongodb+srv://…"}
                     data-testid="refresh-source-url"/>
            </div>
            <div>
              <label className="field-label">Source database</label>
              <input className="w-full border px-3 py-2 font-mono text-xs outline-none"
                     style={{ borderColor: "var(--border-strong)", background: "var(--bg)" }}
                     value={form.source_db} disabled={form.in_place}
                     onChange={(e) => setForm({ ...form, source_db: e.target.value })}
                     placeholder={info.source_db || "production db name"}
                     data-testid="refresh-source-db"/>
            </div>
            <div>
              <label className="field-label">Type REFRESH to confirm</label>
              <input className="w-full border px-3 py-2 text-sm outline-none"
                     style={{ borderColor: "var(--border-strong)", background: "var(--bg)" }}
                     value={form.confirm}
                     onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                     data-testid="refresh-confirm"/>
            </div>
          </div>
          <label className="mt-4 flex items-center gap-2 text-sm" style={{ color: "var(--ink)" }}>
            <input type="checkbox" checked={form.in_place}
                   onChange={(e) => setForm({ ...form, in_place: e.target.checked })}
                   data-testid="refresh-in-place"/>
            Scrub this environment's own database in place (no source needed — for testing the scrub)
          </label>
          <div className="mt-5 flex flex-wrap gap-3">
            <button onClick={() => run(true)} className="btn-ghost flex items-center gap-2"
                    disabled={busy === "dry"} data-testid="refresh-dry-run">
              <ListChecks size={14}/> {busy === "dry" ? "Checking…" : "Report only"}
            </button>
            <button onClick={() => run(false)} className="btn-primary flex items-center gap-2"
                    disabled={busy === "run"} data-testid="refresh-run">
              <RefreshCw size={14}/> {busy === "run" ? "Refreshing…" : "Refresh dev data"}
            </button>
          </div>
        </>
      )}

      {report && (
        <div className="mt-6" data-testid="refresh-report">
          <div className="grid grid-cols-2 border md:grid-cols-4" style={{ borderColor: "var(--border)" }}>
            <Cell label="Mode" value={report.dry_run ? "Report only" : report.mode} testId="refresh-mode"/>
            <Cell label="Documents" value={report.documents_written} testId="refresh-documents"/>
            <Cell label="Employers renamed" value={report.companies_renamed} testId="refresh-companies"/>
            <Cell label="Emails replaced" value={report.emails_replaced} testId="refresh-emails"/>
          </div>
          <div className="mt-4 border overflow-x-auto" style={{ borderColor: "var(--border)" }}>
            <table className="w-full text-sm min-w-[420px]" data-testid="refresh-collections-table">
              <thead>
                <tr className="border-b eyebrow text-left" style={{ borderColor: "var(--border)" }}>
                  <th className="px-4 py-2.5">Collection</th>
                  <th className="px-4 py-2.5">Documents</th>
                </tr>
              </thead>
              <tbody>
                {report.collections.map((entry) => (
                  <tr key={entry.collection} className="border-b" style={{ borderColor: "var(--border)" }}>
                    <td className="px-4 py-2 font-mono text-xs" style={{ color: "var(--ink)" }}>{entry.collection}</td>
                    <td className="px-4 py-2" style={{ color: "var(--muted)" }}>{entry.documents}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs" style={{ color: "var(--muted)" }}>
            Not copied: {report.skipped_collections.join(", ")}
          </p>
        </div>
      )}
    </section>
  );
}

function Cell({ label, value, testId }) {
  return (
    <div className="border-r px-4 py-3 last:border-r-0" style={{ borderColor: "var(--border)" }} data-testid={testId}>
      <div className="eyebrow text-[9px]">{label}</div>
      <div className="mt-1 font-heading text-lg font-bold" style={{ color: "var(--ink)" }}>{value}</div>
    </div>
  );
}

function IssueTable({ title, rows, testId }) {
  return (
    <div className="mt-6">
      <div className="eyebrow mb-2">{title}</div>
      <div className="border overflow-x-auto" style={{ borderColor: "var(--border)" }}>
        <table className="w-full text-sm min-w-[520px]" data-testid={testId}>
          <thead>
            <tr className="border-b eyebrow text-left" style={{ borderColor: "var(--border)" }}>
              <th className="px-4 py-2.5">Row</th>
              <th className="px-4 py-2.5">Email</th>
              <th className="px-4 py-2.5">Detail</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={`${row.row}-${index}`} className="border-b" style={{ borderColor: "var(--border)" }}>
                <td className="px-4 py-2.5" style={{ color: "var(--muted)" }}>{row.row}</td>
                <td className="px-4 py-2.5" style={{ color: "var(--ink)" }}>{row.email}</td>
                <td className="px-4 py-2.5" style={{ color: "var(--muted)" }}>{row.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
